import { RecordType } from '@stylehaven/components';
import { DeepPartial } from '../../entities/common/deep-partial-type.entity';
import { RecordStatus } from './record-status.type';

export enum Family {
    Descriptive = 'Descriptive',
    Dynamic = 'Dynamic',
    Administrative = 'Administrative',
    Technical = 'Technical',
    Internal = 'Internal',
    Structural = 'Structural',
    RightsManagement = 'RightsManagement',
    Context = 'Context'
}

export interface Metadata extends ContextHolder {
    MergeStrategies?: MergeStrategies;
    Descriptive: DescriptiveMetadata;
    Dynamic: DynamicMetadata;
    Administrative: AdministrativeMetadata;
    Technical: TechnicalMetadata;
    Internal: InternalMetadata;
    Structural: StructuralMetadata;
    RightsManagement: RightsManagementMetadata;
    Workflow: WorkflowMetadata;
}

export enum MediaType {
    metadataonly = 'metadataonly',
    video = 'video',
    videofragment = 'videofragment',
    audio = 'audio',
    audiofragment = 'audiofragment',
    image = 'image',
    collection = 'collection',
    set = 'set',
    newspaper = 'newspaper',
    newspaperpage = 'newspaperpage'
}

export enum RelationType {
    IsHeldBy = 'IsHeldBy',
    Holds = 'Holds',
    Requests = 'Requests',
    IsRequestedBy = 'IsRequestedBy',
    IsStoredOn = 'IsStoredOn',
    Stores = 'Stores',
    Contains = 'Contains'
}

export const RelationInverse: Record<RelationType, RelationType> = {
    [RelationType.IsHeldBy]: RelationType.Holds,
    [RelationType.Holds]: RelationType.IsHeldBy,
    [RelationType.Requests]: RelationType.IsRequestedBy,
    [RelationType.IsRequestedBy]: RelationType.Requests,
    [RelationType.IsStoredOn]: RelationType.Stores,
    [RelationType.Stores]: RelationType.IsStoredOn,
    [RelationType.Contains]: RelationType.Contains
};

export type FamilyMetadata =
    | DescriptiveMetadata
    | DynamicMetadata
    | AdministrativeMetadata
    | TechnicalMetadata
    | InternalMetadata
    | StructuralMetadata
    | RightsManagementMetadata
    | WorkflowMetadata
    | ContextMetadata;

export type UUID = string;

enum GroupIdBrand {
    _ = ''
}
export type GroupId = string & GroupIdBrand;

enum UserIdBrand {
    _ = ''
}
export type UserId = string & UserIdBrand;

enum IngestSpaceIdBrand {
    _ = ''
}
export type IngestSpaceId = string & IngestSpaceIdBrand;

enum FragmentIdBrand {
    _ = ''
}
export type FragmentId = string & FragmentIdBrand;

enum MediaObjectIdBrand {
    _ = ''
}

export type MediaObjectId = string & MediaObjectIdBrand;

enum MediaObjectIdWithNumberBrand {
    _ = ''
}
export type MediaObjectIdWithNumber = string & MediaObjectIdWithNumberBrand;

export type Identifier = FragmentId | MediaObjectId;

enum Md5Brand {
    _ = ''
}
export type Md5 = string & Md5Brand;

export enum OriginalStatus {
    in_progress = 'in_progress',
    completed = 'completed',
    failed = 'failed'
}

export { RecordType } from '@stylehaven/components';

export enum BrowseStatus {
    in_progress = 'in_progress',
    completed = 'completed',
    cancelled = 'cancelled',
    failed = 'failed',
    no_browse = 'no_browse'
}
export enum ArchiveStatus {
    in_progress = 'in_progress',
    on_disk = 'on_disk',
    failed = 'failed',
    completed = 'completed'
}
export type TimeCode = string;

export enum IsInIngestspace {
    true = '1',
    false = '0',
    both = '*'
}

export interface Keywords {
    Keyword: string[];
}
export interface Categories {
    Category: string[];
}

export interface DescriptiveMetadata {
    Title: string;
    OriginalFilename: string | null;
    UploadedBy: string | null;
    KeyframeStart: number;
    Location: string | null;
    Description: string | null;
    CreationDate: string | null;
    RightsOwner: string | null;
    Rights: string | null;
    Keywords: Keywords;
    Categories: Categories;
    Publisher: string | null;
    Authors: Record<string, string[]>;
    Address: Record<string, any>;
    NonPreferredTerm: string | null;
    Publications: string | null;
}

export interface DynamicMetadata {
    [key: string]: any;
    SeriesDisplayName?: string;
    Carrier?: string;
    /**
     * On a stuk: it describes a physical original. Says nothing about whether a digital representatie exists
     * next to it — only the representaties themselves do (see `DigitalCarrierService`).
     */
    DocumentAnalog?: boolean;
    SeriesRetentionPeriodAdministrative?: {
        Defined: boolean;
        Specification: string;
        Term: number;
        Unit: string;
        Motivation: string;
    };
    RetentionPeriodUpdate?: {
        Term: number;
        Unit: string;
        Motivation: string;
    };
    DossierName?: string;
}

export interface AdministrativeMetadata {
    readonly OrganisationName: string | null;
    readonly OrganisationLongName: string | null;
    readonly OrganisationExternalId: string | null;
    readonly LastModifiedDate: string;
    readonly UserLastModifiedDate: string;
    readonly ArchiveDate: string;
    readonly Type: MediaType;
    readonly IsSynchronized: boolean;
    readonly IsPreservation: boolean;
    readonly IsOriginal: boolean;
    readonly IsAccess: boolean;
    readonly HasFailedJobs: boolean;
    readonly PublicationDate: string | null;
    readonly ExternalId: string | null;
    readonly DepartmentName: string | null;
    readonly Workflow: string | null;
    readonly IngestTape: string | null;
    readonly RecordType: RecordType;
    readonly MainRecordType: RecordType;
    readonly RecordStatus: RecordStatus;
    readonly ExternalUrl: string | null;
    readonly RecordRejections?: {
        Rejection: Rejection[];
    };
}

export interface TechnicalMetadata {
    readonly FileSize: number;
    readonly PronomId: string | null;
    readonly OriginalExtension: string | null;
    readonly Md5: Md5 | null;
    readonly MimeType: string | null;
    readonly Width: number | null;
    readonly Height: number | null;
    readonly ImageSize: number | null;
    readonly ImageOrientation: string | null;
    readonly ImageQuality: string | null;
    readonly VideoTechnical: any | null;
    readonly AudioTechnical: any | null;
    readonly VideoFormat: any | null;
    readonly DurationTimeCode: TimeCode | null;
    readonly StartTimeCode: TimeCode | null;
    readonly EndTimeCode: TimeCode | null;
    readonly DurationFrames: number | null;
    readonly StartFrames: number | null;
    readonly EndFrames: number | null;
    readonly VideoCodec: string | null;
    readonly VideoFps: any | null;
    readonly VideoBitRate: any | null;
    readonly AudioCodec: any | null;
    readonly AudioChannels: any | null;
    readonly AudioSampleRate: any | null;
    readonly AudioBitRate: any | null;
    readonly AudioTracks: any | null;
    readonly Origin: any | null;
    readonly EssenceOffset: any | null;
    readonly EditUnitByteSize: any | null;
    readonly RunIn: any | null;
    readonly FramesPerEditUnit: any | null;
    readonly EditRate: any | null;
    readonly IndexEditRate: any | null;
    readonly BitRate: any | null;
    readonly Exif?: Record<string, string>;
}

export interface InternalMetadata {
    readonly MediaObjectId: MediaObjectId;
    readonly RecordId: MediaObjectId;
    readonly FragmentId: FragmentId;
    readonly OriginalStatus: OriginalStatus;
    readonly BrowseStatus: BrowseStatus;
    readonly ArchiveStatus: ArchiveStatus;
    readonly UploadedById: UserId | null;
    readonly OrganisationId: string;
    readonly IngestSpaceId: IngestSpaceId | null;
    readonly IsInIngestSpace: boolean;
    readonly IsFragment: boolean;
    readonly HasKeyframes: boolean;
    readonly ContainsGeoData: boolean;
    readonly PathToKeyframe: string;
    readonly PathToKeyframeThumb: string;
    readonly DepartmentId: GroupId | null;
    readonly Browses: any | null;
    readonly PathToVideo: string | null;
}

export interface StructuralMetadata {
    Relations?: Record<string, FragmentId[] | null>;
    ReferenceCodes: {
        [key: string]: string[];
    };
    ReferenceTitles: {
        [key: string]: string[];
    };
    Versioning: {
        Id: string;
        Status: string;
        Version: number;
    };
    Sets: { Set: MediaObjectIdWithNumber[] };
    Collections: { Collection: MediaObjectIdWithNumber[] };
    Newspapers: { Newspaper: FragmentId[] };
    MainFragment: any;
    Fragments: any;
    FragmentStartFrames: number;
    FragmentEndFrames: number;
    FragmentDurationFrames: number | null;
    FragmentDurationTimeCode: TimeCode | null;
    FragmentStartTimeCode: TimeCode | null;
    FragmentEndTimeCode: TimeCode | null;
    ParentRecordId?: string;
    HasNonRepresentationChildren: boolean;
    OriginalRecordId?: string;
}

export interface RightsManagementMetadata {
    readonly Permissions: Permissions;
    readonly ExpiryDate: string | null;
    readonly ExpiryStatus: string | null;
}

export interface Permissions {
    readonly Read: GroupId[];
    readonly Write: GroupId[];
    readonly Export: GroupId[];
}

export interface ContextMetadata {
    readonly Profiles: string[];
    readonly IsEditable?: boolean;
    readonly IsDeletable?: boolean;
    readonly IsPublic?: boolean;
    readonly IsExportable?: boolean;
    readonly IsViewable?: boolean;
    readonly IsSuspendable?: boolean;
    readonly CanStopSuspension?: boolean;
    readonly IsInterruptible?: boolean;
    readonly CanApproveDestruction?: boolean;
    readonly CanEditAdministrativeMetadata?: boolean;
    readonly IsAcceptable?: boolean;
    readonly CanPublishDirectly?: boolean;
    readonly CanBePutBack?: boolean;
    readonly CanBeExpired?: boolean;
    readonly CanBeRequestedInDepot?: boolean;
    readonly IsSubmissible?: boolean;
    readonly CanBeLinkedToPackagingUnit?: boolean;
    readonly CanBeLinkedToTransportUnit?: boolean;
    readonly CanBeLinkedToTransport?: boolean;
    readonly CanBeReceived?: boolean;
    readonly CanStore?: boolean;
    readonly CanBeStored?: boolean;
    readonly IsResubmissible?: boolean;
    readonly CanLinkRepresentations?: boolean;
    readonly Reasons: ContextReasons;
    readonly CanBeSealed?: boolean;
    readonly CanSubmitReview?: boolean;
    readonly [key: string]: boolean | ContextReasons | string[] | undefined;
}

export interface ContextReasons {
    readonly IsEditable?: ContextReason[];
    readonly IsDeletable?: ContextReason[];
    readonly IsPublic?: ContextReason[];
    readonly IsExportable?: ContextReason[];
    readonly IsViewable?: ContextReason[];
    readonly IsSuspendable?: ContextReason[];
    readonly CanStopSuspension?: ContextReason[];
    readonly IsInterruptible?: ContextReason[];
    readonly CanApproveDestruction?: ContextReason[];
    readonly CanEditAdministrativeMetadata?: ContextReason[];
    readonly IsAcceptable?: ContextReason[];
    readonly CanPublishDirectly?: ContextReason[];
    readonly CanBeExpired?: ContextReason[];
    readonly CanBeRequestedInDepot?: ContextReason[];
    readonly CanBePutBack?: ContextReason[];
    readonly CanSubmitReview?: ContextReason[];
    readonly [key: string]: ContextReason[] | undefined;
}

export interface ContextReason {
    readonly Code: string;
    readonly Value: string;
}

export interface ContextHolder {
    Context: ContextMetadata;
}

export interface WorkflowMetadata {
    Tasks: {
        Task: WorkflowTask[];
    };
}

export interface WorkflowTask {
    Id: string;
    ProcessId: string;
    ProcessName: string;
    TypeId: string;
    TypeName: string;
}

export type MergeStrategies = Record<string, MergeStrategy>;

export enum MergeStrategy {
    'OVERWRITE' = 'OVERWRITE',
    'KEEP' = 'KEEP',
    'MERGE' = 'MERGE'
}

export interface MHLocation {
    Address: Record<string, string> | undefined;
    Location: string | null;
}

export type PartialMetadata = DeepPartial<Metadata>;

export interface AnalogRepresentation {
    Status: string;
    PackagingUnits: {
        PackagingUnit: AnalogRepresentationPackagingUnit[];
        Total: number;
    };
}

export interface AnalogRepresentationPackagingUnit {
    Number: number;
    Id: string;
}

/* temporary hack */
export enum MH2Types {
    Mh2Collection = 'Mh2Collection'
}

export type MH2MediaType = MediaType | MH2Types;

export interface Rejection {
    Reason: string;
    Motivation?: string;
    Date: string;
    User: string;
    Path?: string;
    Row?: number;
    Field?: string;
    Value?: string;
}
